import React from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useMutation } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

// StyledTextField for consistent styling with responsive padding
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& input': {
    padding: '8px 12px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 12px'
    }
  },
  width: '100%'
}))

interface RuleFormProps {
  handleClose: () => void
}

const NewRuleForm: React.FC<RuleFormProps> = ({ handleClose }) => {
  // Rule type options
  const ruleTypeOptions = [
    'CATALOG_ITEM',
    'CATALOG_ITEM_EXCLUSION',
    'MAX_USAGE_PER_USER',
    'MIN_BULK_PURCHASE',
    'MIN_CATALOG_PRICE',
    'FIRST_PURCHASE_ONLY'
  ]

  // Function to get placeholder text based on rule type
  const getRuleValuePlaceholder = (ruleType) => {
    switch (ruleType) {
      case 'CATALOG_ITEM':
        return 'Enter Id of catalog'
      case 'CATALOG_ITEM_EXCLUSION':
        return 'Enter Id of catalog'
      case 'MAX_USAGE_PER_USER':
        return 'Enter the value'
      case 'MIN_BULK_PURCHASE':
        return 'Enter the value'
      case 'MIN_CATALOG_PRICE':
        return 'Enter catalog price'
      case 'FIRST_PURCHASE_ONLY':
        return 'Enter the value'
      default:
        return 'Enter rule value'
    }
  }

  // Function to get default value for rule type
  const getDefaultRuleValue = (ruleType) => {
    return ruleType === 'FIRST_PURCHASE_ONLY' ? '1' : ''
  }

  // Updated validation schema - all fields required
  const RuleFormSchema = Yup.object().shape({
    ruleName: Yup.string().required('Rule name is required'),
    ruleDescription: Yup.string().required('Rule description is required'),
    ruleType: Yup.string().required('Rule type is required'),
    ruleValue: Yup.string().required('Rule value is required')
  })

  const initialValues = {
    ruleName: '',
    ruleDescription: '',
    ruleType: '',
    ruleValue: ''
  }
  const faberToken = OrbitSession.get('faberToken')
  const { mutate: createRewardRule } = useMutation(
    RewardsAPI.createRewardRule,
    {
      onSuccess: () => {
        toast.success('Reward rule created successfully!')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating promo code')
      }
    }
  )

  const handleFormSubmit = (values) => {
    const allValues = {
      ruleName: values.ruleName,
      ruleDescription: values.ruleDescription,
      ruleType: values.ruleType,
      ruleValue: values.ruleValue,
      faberToken: faberToken
    }
    createRewardRule(allValues)
  }
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={RuleFormSchema}
      onSubmit={handleFormSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        errors,
        touched,
        setFieldValue
      }) => (
        <Form>
          <Box sx={{ padding: { xs: 2, sm: 0 } }}>
            <Grid container rowSpacing={2} columnSpacing={2}>
              {/* Rule Name */}
              <Grid item xs={12} sm={3} md={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 'auto', sm: '40px' }
                  }}
                >
                  <NormalText isBold>Name *</NormalText>
                </Box>
              </Grid>
              <Grid item xs={12} sm={9} md={9}>
                <Box sx={{ width: { xs: '100%', sm: '80%' } }}>
                  <StyledTextField
                    name='ruleName'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    value={values.ruleName}
                    inputProps={{
                      'data-testid': 'rule-name'
                    }}
                    fullWidth
                    variant='outlined'
                    required
                    error={!!(errors.ruleName && touched.ruleName)}
                    helperText={
                      errors.ruleName && touched.ruleName
                        ? errors.ruleName
                        : null
                    }
                  />
                </Box>
              </Grid>

              {/* Rule Description */}
              <Grid item xs={12} sm={3} md={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 'auto', sm: '40px' }
                  }}
                >
                  <NormalText isBold>Description *</NormalText>
                </Box>
              </Grid>
              <Grid item xs={12} sm={9} md={9}>
                <Box sx={{ width: { xs: '100%', sm: '80%' } }}>
                  <StyledTextField
                    name='ruleDescription'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    value={values.ruleDescription}
                    inputProps={{
                      'data-testid': 'rule-description'
                    }}
                    fullWidth
                    variant='outlined'
                    required
                    error={
                      !!(errors.ruleDescription && touched.ruleDescription)
                    }
                    helperText={
                      errors.ruleDescription && touched.ruleDescription
                        ? errors.ruleDescription
                        : null
                    }
                  />
                </Box>
              </Grid>

              {/* Rule Type */}
              <Grid item xs={12} sm={3} md={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 'auto', sm: '40px' }
                  }}
                >
                  <NormalText isBold>Rule Type *</NormalText>
                </Box>
              </Grid>
              <Grid item xs={12} sm={9} md={9}>
                <Box sx={{ width: { xs: '100%', sm: '80%' } }}>
                  <FormControl
                    fullWidth
                    variant='standard'
                    error={!!(errors.ruleType && touched.ruleType)}
                  >
                    <Select
                      value={values.ruleType}
                      onChange={(e) => {
                        const newRuleType = e.target.value
                        setFieldValue('ruleType', newRuleType)
                        // Set default value for MIN_CATALOG_PRICE, clear for others
                        setFieldValue(
                          'ruleValue',
                          getDefaultRuleValue(newRuleType)
                        )
                      }}
                      name='ruleType'
                      required
                      displayEmpty
                    >
                      <MenuItem value='' disabled>
                        Select a rule type
                      </MenuItem>
                      {ruleTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option.replace(/_/g, ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.ruleType && touched.ruleType && (
                      <Box
                        sx={{
                          color: 'error.main',
                          fontSize: '0.75rem',
                          mt: 0.5
                        }}
                      >
                        {errors.ruleType}
                      </Box>
                    )}
                  </FormControl>
                </Box>
              </Grid>

              {/* Rule Value */}
              <Grid item xs={12} sm={3} md={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 'auto', sm: '40px' }
                  }}
                >
                  <NormalText isBold>Rule Value *</NormalText>
                </Box>
              </Grid>
              <Grid item xs={12} sm={9} md={9}>
                <Box sx={{ width: { xs: '100%', sm: '80%' } }}>
                  <StyledTextField
                    name='ruleValue'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    value={values.ruleValue}
                    placeholder={getRuleValuePlaceholder(values.ruleType)}
                    inputProps={{
                      'data-testid': 'rule-value'
                    }}
                    fullWidth
                    variant='outlined'
                    required
                    error={!!(errors.ruleValue && touched.ruleValue)}
                    helperText={
                      errors.ruleValue && touched.ruleValue
                        ? errors.ruleValue
                        : null
                    }
                  />
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  mt={3}
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}
                >
                  <Button
                    variant='contained'
                    type='submit'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: { xs: '300px', sm: 'none' }
                    }}
                  >
                    Save Rule
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  )
}

export default NewRuleForm
